// ES modules
import React, { useState } from "react";
import ReactDOMServer from 'react-dom/server';

import * as styles from "./blog-post.module.css";
import { differenceInDays, formatDistance, format } from "date-fns";
import AuthorList from "./author-list";
import Container from "./container";
import PortableText from "../../core/sanity/content/portableText";
import { buildImageObj } from "../../core/sanity/lib/helpers";
import { imageUrlFor } from "../../core/sanity/lib/image-url";

import 'gitalk/dist/gitalk.css'
import GitalkComponent from "gitalk/dist/gitalk-component";

import Fade from 'react-reveal/Fade'

import {
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  TwitterShareButton,
  TwitterIcon,
  RedditShareButton,
  RedditIcon
} from 'react-share'

import shareLink from './assets/ShareLink.svg';
import shareLinkSuccess from './assets/ShareSuccess.svg';

import { Breadcrumb } from 'gatsby-plugin-breadcrumb';
import { BreadcrumbContainer } from "../../core/NavigationStyles";

import styled from 'styled-components';

const readingTime = require('reading-time'); 

// check window is not defined
const isBrowser = typeof window !== "undefined";

const SharebuttonsContainer = styled.span`
    display: inline-block;
    height: 100%; 
    vertical-align: middle;
    margin-left: 15px;

    @media (max-width: 456px) {
      margin-left: 0px;
      margin-right: 15px;
      margin-top: 10px;

      vertical-align: text-top;
    }
  `;

function ShareIcons({ url, title }) {
  const [shareSuccess, setShareSuccess] = useState(false);

  /**
   * Allows users to copy blog to their clipboard and share them
  */
  const handleShareLink = (url, title, description) => {
    if (isBrowser) {
      // use old version if navigator clipboard doesn't exist
      // execCommand is now depreciated
      if (!navigator.clipboard) {
        const tempUrlData = document.createElement('textarea');
        tempUrlData.value = `${url}`;
        document.body.appendChild(tempUrlData);
        tempUrlData.select();
        // copy command still functions appropriately for copy commands
        // if copy is successful
        if (document.execCommand('copy')) {
          document.body.removeChild(tempUrlData);
          // setShareSuccess(!shareSuccess);
          setShareSuccess(true);

        } else {
          alert('Could not copy to clipboard');
          setShareSuccess(false);
        }
      // new version if it does exist 
      // not implemented in all browsers so both methods are used
      } else {
        navigator.clipboard.writeText(url).then(
          function(){
            // setShareSuccess(!shareSuccess);
            setShareSuccess(true);
          })
        .catch(
          function() {
            alert("Could not copy url"); // error
            setShareSuccess(false);
        });
      }
    }
  }

  return (
  <SharebuttonsContainer>
    <LinkedinShareButton style={{ marginRight: '0.5rem' }} url={url} >
        <LinkedinIcon  size={25} round={true}/>
    </LinkedinShareButton>
    
    <FacebookShareButton style={{ marginRight: '0.5rem' }} url={url} >
      <FacebookIcon  size={25} round={true}/>
    </FacebookShareButton>
    
    <TwitterShareButton style={{ marginRight: '0.5rem' }} url={url} title={title}>
      <TwitterIcon  size={25} round={true} />
    </TwitterShareButton>

    <RedditShareButton style={{ marginRight: '0.5rem' }} url={url} title={title} >
      <RedditIcon  size={25} round={true} />
    </RedditShareButton>

    <button onClick={() => handleShareLink(url, title)} style={{cursor: 'pointer', background: 'transparent', padding: '0', border: 'none'}}>
      <img src={shareSuccess ? shareLinkSuccess : shareLink} height={25} width={25} alt='Button copy link to share blog'/>
      {/* {shareSuccess && <div style={{position: 'absolute', color: 'white', pointerEvents: 'none', marginLeft: '-10px'}}>
        Copied!
      </div>} */}
    </button>
  </SharebuttonsContainer>);
}

function BlogPost(props) {
  const {
    _rawBody,
    // _rawExcerpt,
    authors,
    //categories,
    title,
    mainImage,
    publishedAt,
  } = props;

  // converts entire raw body into static HTML to pass into reading time function
  // allows appropriate estimation of actual body
  const articleStats = readingTime(ReactDOMServer.renderToStaticMarkup(<PortableText blocks={_rawBody} />), { wordsPerMinute: 265 });

  return (
    <article className={styles.root}>
      <Fade>
        <Container>
          <BreadcrumbContainer>
            <Breadcrumb style={{color: 'var(--color-gray)'}} location={props.location} crumbLabel={props.title || "Untitled"} />
          </BreadcrumbContainer>

          <div className={styles.grid}>
            <div className={styles.mainContent}>
              <div className={styles.title}>{title}</div>
              {publishedAt && (
                <div className={styles.publishedAt}>
                  {differenceInDays(new Date(publishedAt), new Date()) > 3
                    ? formatDistance(new Date(publishedAt), new Date())
                    : format(new Date(publishedAt), "MMMM do, yyyy ")}
                  &#183; {articleStats.text} {isBrowser && <ShareIcons url={window.location.href} title={title}/>}
                </div>
              )}
              {mainImage && mainImage.asset && (
                <>
                  <div className={styles.mainImage}>
                    <img
                      src={imageUrlFor(buildImageObj(mainImage))
                        .width(1200)
                        .height(Math.floor((9 / 16) * 1200))
                        .fit("crop")
                        .auto("format")
                        .url()}
                      alt={mainImage.alt}
                    />
                  </div>
                  <figcaption style={{color: 'var(--color-gray)', marginTop: '10px'}}>
                    {mainImage.caption}
                  </figcaption>
                </>
              )}
              <aside className={styles.metaContent}>
                {authors && <AuthorList items={authors} title="By" />}
              </aside>
              {_rawBody && <PortableText blocks={_rawBody} />}
            </div>
            {/* <aside>
              {categories && (
                <div className={styles.categories}>
                  <h3 className={styles.categoriesHeadline}>Categories</h3>
                  <ul>
                    {categories.map((category) => (
                      <li key={category._id}>{category.title}</li>
                    ))}
                  </ul>
                </div>
              )}
            </aside> */}
          </div>
          {isBrowser && 
            <GitalkComponent options={{
              clientID: "ee9813daec14b517f307",
              clientSecret: 'ae17674e0e1f9d25d0779339c0abfe3c2e901604',
              repo: 'personal-blog-comments',      // The repository of store comments,
              owner: 'jnddao',
              admin: ['jnddao'],
              id: props.slug.current,      // Ensure uniqueness and length less than 50
              distractionFreeMode: false,  // Facebook-like distraction free mode
              createIssueManually: false
            }} />
          }
        </Container>
      </Fade>
    </article>
  );
}

export default BlogPost;
